// Return true when monthly chat credits are used up.
export function hasNoChatCreditsLeft(chatCredit) {
  const credit = chatCredit && typeof chatCredit === "object" ? chatCredit : null;
  if (!credit || credit.total == null) return false;
  return Number(credit.total) === 0;
}

// Decide which limit to show from landing chat_credit data.
export function parseChatCreditLimit(landingData) {
  const credit = landingData && landingData.chat_credit;

  if(landingData.subscription_status === 'active'){
    return {
      isShowLimitUsageBanner: false,
      isShowLimitedAccessComposer: false,
    };
  }

  if (!credit || credit.total == null) {
    return {
      isShowLimitUsageBanner: false,
      isShowLimitedAccessComposer: false,
    };
  }

  const total = Number(credit.total);
  return {
    isShowLimitUsageBanner: total > 0,
    isShowLimitedAccessComposer: hasNoChatCreditsLeft(credit),
  };
}

export const CHAT_CREDIT_DECREASE_AFTER_SUBMIT = 1;

// Update landing chat_credit: replace total from API or decrease by amount.
export function syncChatCredit(landingData, options) {
  const credit = landingData && landingData.chat_credit;
  if (!credit || credit.total == null) return landingData;

  const currentTotal = Math.max(0, Number(credit.total));
  let nextTotal = currentTotal;

  if (options && options.total != null) {
    nextTotal = Math.max(0, Number(options.total));
  } else if (options && options.decreaseBy != null) {
    nextTotal = Math.max(0, currentTotal - Number(options.decreaseBy));
  }

  return Object.assign({}, landingData, {
    chat_credit: Object.assign({}, credit, { total: nextTotal }),
  });
}

let isMarkedConfigured = false;

// Convert markdown text to HTML using the global marked library from the AI layout CDN.
export function convertMarkdownToHtml(text) {
  const markdownSource = text == null ? "" : String(text);
  const markedGlobal =
    typeof window !== "undefined" ? window.marked : undefined;

  if (markedGlobal && typeof markedGlobal.parse === "function") {
    try {
      if (!isMarkedConfigured) {
        markedGlobal.setOptions({ breaks: true, gfm: true });
        isMarkedConfigured = true;
      }
      return markedGlobal.parse(markdownSource);
    } catch (parseError) {
      console.error(
        "[convertMarkdownToHtml] marked.parse failed, trying legacy API:",
        parseError
      );
    }
  }

  if (typeof markedGlobal === "function") {
    try {
      return markedGlobal(markdownSource);
    } catch (legacyError) {
      console.error(
        "[convertMarkdownToHtml] Legacy marked() failed, falling back to plain text:",
        legacyError
      );
    }
  }

  return markdownSource.replace(/\n/g, "<br>");
}

// Join answer message parts from the API into one markdown string.
function joinAnswerMessages(messages) {
  if (!Array.isArray(messages)) return "";

  return messages
    .map((item) => (item && item.message != null ? String(item.message) : ""))
    .filter(Boolean)
    .join("\n\n");
}

// Map one API chat item into a turn object for chat-bubble rendering.
export function mapApiChatToTurn(chat) {
  if (!chat || !chat.type) return null;

  const messages = Array.isArray(chat.messages) ? chat.messages : [];
  const firstText =
    messages[0] && messages[0].message != null ? String(messages[0].message) : "";

  if (chat.type === "question") {
    return {
      id: String(chat.id),
      type: "question",
      text: firstText,
      imageUrls: Array.isArray(chat.image_urls) ? chat.image_urls : [],
      fileNames: Array.isArray(chat.file_names) ? chat.file_names : [],
      answerId: chat.answer_id != null ? String(chat.answer_id) : "",
    };
  }

  if (chat.type === "answer") {
    const answerText = joinAnswerMessages(messages);

    return {
      id: String(chat.id),
      type: "answer",
      html: convertMarkdownToHtml(answerText),
      questionId: chat.question_id != null ? String(chat.question_id) : "",
    };
  }

  return null;
}

// Build EventSource URL for AI chat SSE. Empty sseBaseUrl keeps same-origin path.
export function buildAiChatSseUrl(sseBaseUrl, topicId, uid) {
  const params = new URLSearchParams();
  params.set("topic_id", String(topicId));
  params.set("uid", String(uid));
  const path = `/sse/ai-chat?${params.toString()}`;
  const base =
    sseBaseUrl != null ? String(sseBaseUrl).trim().replace(/\/$/, "") : "";
  return base ? `${base}${path}` : path;
}
